/**
 * InfinitSales - Assignment Service
 *
 * Responsável pelo contato (membro humano ou agente de IA) + histórico de
 * transferências. Endpoints: /api/contacts/*. O evento SSE
 * CONTACT_ASSIGNMENT_CHANGED mantém o estado em tempo real após a carga
 * inicial via getAll().
 */
import api from './api';
import requestCache from './requestCache';

const assignmentService = {
  /** Alvos válidos de transferência: membros da equipe + agente do workspace.
   *  Cache 60s + dedup em voo: os 2 ChatLists (que remontam a CADA conversa
   *  no mobile), o TransferContactModal e o AgentToggleModal refaziam este GET
   *  — e no servidor cada chamada custa até 2 lookups por membro. Conexões/
   *  equipe mudam raramente; o backend já tem cache de 300s por cima. */
  async getTargets() {
    return requestCache.cachedGet(
      'assignment:targets',
      async () => {
        const res = await api.get('/api/contacts/assignment/targets');
        return res.data;
      },
      { ttl: 60_000 },
    );
  },

  /** Mapa {phone: assigned_to} de todos os contatos com responsável. */
  async getAll() {
    const res = await api.get('/api/contacts/assignments');
    return res.data;
  },

  async get(phone) {
    const res = await api.get(`/api/contacts/${encodeURIComponent(phone)}/assignment`);
    return res.data;
  },

  /** Responsavel de um LOTE de conversas de Instagram/Messenger (Bloco A3):
   *  uma chamada por ate 200 referencias, em vez de um GET por cartao.
   *  Responde 404 quando a flag do backend esta desligada; quem chama volta
   *  ao GET por cartao. */
  async getByRefs(refs) {
    const lista = Array.from(new Set((refs || []).filter(Boolean)));
    const out = {};
    for (let i = 0; i < lista.length; i += 200) {
      const res = await api.post('/api/contacts/assignments/by-refs', {
        refs: lista.slice(i, i + 200),
      });
      Object.assign(out, res.data?.data || {});
    }
    return out;
  },

  /** Transfere o contato. target = {type: 'member'|'agent', id}, reason opcional. */
  /**
   * Atribui um responsável (membro ou agente) a TODOS os contatos de uma
   * etapa do funil ('unlabeled' = coluna Sem etapa). STAGE_BULK_ASSIGN_ENABLED.
   */
  async assignStage({ labelId, pipelineId, target, reason = '' }) {
    const res = await api.post('/api/contacts/assignments/by-stage', {
      label_id: labelId,
      pipeline_id: pipelineId || null,
      type: target.type,
      id: target.id,
      reason: reason || null,
    });
    return res.data;
  },

  async assign(phone, target, reason) {
    const res = await api.put(`/api/contacts/${encodeURIComponent(phone)}/assignment`, {
      type: target.type,
      id: target.id,
      reason: reason || undefined,
    });
    return res.data;
  },

  async clear(phone) {
    const res = await api.delete(`/api/contacts/${encodeURIComponent(phone)}/assignment`);
    return res.data;
  },

  async getHistory(phone, limit = 100) {
    const res = await api.get(
      `/api/contacts/${encodeURIComponent(phone)}/assignment/history?limit=${limit}`
    );
    return res.data;
  },

  /** Lead já cadastrado? Lookup por telefone na company (criar-lead). Retorna
   *  o body { data: { exists, contact, responsible, error } }. */
  async lookup(phone) {
    const res = await api.get(`/api/contacts/lookup?phone=${encodeURIComponent(phone)}`);
    return res.data;
  },

  /** Atrela o responsável a quem está criando o lead (criar-lead). */
  async assignSelf(phone) {
    const res = await api.post(
      `/api/contacts/${encodeURIComponent(phone)}/assignment/self`
    );
    return res.data;
  },

  /** Automação de entrada (contato novo sem etiqueta/responsável). */
  async getEntryAutomation() {
    const res = await api.get('/api/contacts/entry-automation');
    return res.data;
  },

  /** cfg = {enabled, mode: 'fixed'|'round_robin', fixed_target, pool}. */
  async setEntryAutomation(cfg) {
    const res = await api.put('/api/contacts/entry-automation', cfg);
    return res.data;
  },

  /** Painel de distribuição (round-robin): contagem por destino + recentes.
   *  since = ISO timestamp opcional (filtro de período). */
  async getDistributionStats(since, source = 'automation,agent_handoff,manual_roulette') {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (source) params.set('source', source);
    const qs = params.toString();
    const res = await api.get(`/api/contacts/entry-automation/distribution-stats${qs ? `?${qs}` : ''}`);
    return res.data;
  },

  /** Transfere pela ROLETA (round-robin) de um GRUPO: o backend escolhe o
   *  próximo membro do pool daquele grupo (conta em manual_roulette). */
  async assignRoulette(phone, groupId) {
    const res = await api.post(
      `/api/contacts/${encodeURIComponent(phone)}/assignment/roulette/${encodeURIComponent(groupId)}`
    );
    return res.data;
  },

  /** Grupos de roleta disponíveis ao usuário. Retorna { data: [grupos],
   *  is_owner }. O dono vê todos (com pool); o membro só os allow_members. */
  async getTransferRoulettes() {
    // Cache 60s + dedup em voo: o drawer de etiquetas, a regua de resposta,
    // o hook useTransferRoulettes e o editor de campanha pediam este GET a
    // cada troca de aba. Invalidado no PUT abaixo.
    return requestCache.cachedGet(
      'assignment:transfer-roulettes',
      async () => {
        const res = await api.get('/api/contacts/transfer-roulettes');
        return res.data;
      },
      { ttl: 60_000 },
    );
  },

  /** Salva os grupos de roleta (somente admin). groups = [{id?, name, pool:
   *  [{type:'member', id}], allow_members}]. */
  async setTransferRoulettes(groups) {
    const res = await api.put('/api/contacts/transfer-roulettes', { groups });
    requestCache.invalidate('assignment:transfer-roulettes');
    return res.data;
  },

  /** Funcionalidade B — opções de número de ORIGEM do contato (chip + label
   *  do AI Workspace). Retorna { data: { default_origin, active_origin,
   *  options, allow_own, allow_admin } }. Autorização 100% server-side. */
  async getOriginOptions(phone) {
    const res = await api.get(
      `/api/contacts/${encodeURIComponent(phone)}/origin-options`
    );
    return res.data;
  },

  /** Define/limpa o número de ORIGEM do contato (vale para a equipe). Passe
   *  origin=null (ou sem origin_user_id) para voltar ao número da thread ativa. */
  async setOrigin(phone, origin) {
    const res = await api.put(`/api/contacts/${encodeURIComponent(phone)}/origin`, {
      origin_user_id: origin?.origin_user_id || null,
      channel: origin?.channel || null,
    });
    return res.data;
  },

  /** Origem de ENTRADA do contato: por qual número/membro o lead chegou
   *  (independente do responsável atual). Retorna { data: { origin_member_id,
   *  origin_member_name, origin_is_admin, channel, origin_number,
   *  connection_name } | null }. */
  async getEntryOrigin(phone) {
    const res = await api.get(
      `/api/contacts/${encodeURIComponent(phone)}/entry-origin`
    );
    return res.data;
  },

  /** Contatos de OUTROS canais vinculados a este: o lead que chegou pelo
   *  Instagram/Messenger e foi cadastrado no WhatsApp pelo agente. Retorna
   *  { data: [{ key, channel, linked_at, channels, name, username, phone }] },
   *  só com os vínculos cujo contato ainda existe em algum inbox. */
  async getLinkedContacts(phone) {
    const res = await api.get(
      `/api/contacts/${encodeURIComponent(phone)}/linked-contacts`
    );
    return res.data;
  },

  /** Lead unificado nos 3 canais (flag lead_multichannel): pessoas da empresa
   *  com quem vincular este contato, por nome ou numero. Retorna
   *  { data: [{ company_contact_id, name, photo, channels: [{channel, key, label}] }] }.
   *  404 com a flag desligada. */
  async linkCandidates(key, q) {
    const res = await api.get(
      `/api/contacts/${encodeURIComponent(key)}/link-candidates?q=${encodeURIComponent(q)}`
    );
    return res.data;
  },

  /** Vincula este contato a OUTRO canal da mesma pessoa: os dois viram um
   *  lead so (etapa, responsavel, tags e campos passam a ser os mesmos).
   *  SOMENTE admin da empresa. Retorna { data: { mode, company_contact_id,
   *  channels } }. 409 quando um dos contatos nao existe. */
  async linkChannel(key, otherKey) {
    const res = await api.post(
      `/api/contacts/${encodeURIComponent(key)}/link-channel`,
      { other_key: otherKey }
    );
    return res.data;
  },

  /** EXCLUIR o contato de vez (SOMENTE admin). Purga IRREVERSÍVEL: apaga o
   *  contato/conversa nos 2 canais, mensagens, followups e a conversa da IA.
   *  Retorna { data: { ...contagens } }. */
  /** O que EXCLUIR o contato vai apagar: chaves por canal do lead unificado. */
  async purgePreview(phone) {
    const res = await api.get(`/api/contacts/${encodeURIComponent(phone)}/purge/preview`);
    return res.data?.data || res.data;
  },

  async purgeContact(phone) {
    const res = await api.delete(
      `/api/contacts/${encodeURIComponent(phone)}/purge`
    );
    return res.data;
  },

  /** EDITAR o número do contato (SOMENTE admin): re-chaveia o contato + histórico
   *  para newPhone em todas as partições/2 canais; se newPhone já existir, MESCLA.
   *  Retorna { data: { mode, ...contagens } }. */
  async rekeyContact(phone, newPhone) {
    const res = await api.put(
      `/api/contacts/${encodeURIComponent(phone)}/rekey`,
      { new_phone: newPhone }
    );
    return res.data;
  },
};

export default assignmentService;
